import React, { useState, useEffect, useCallback } from 'react';
import { getSubscriptionPlans } from '../../services/subscriptionService';
import { socketService } from '../../utils/socketService';
import { formatPlanPrice, normalizePlanCurrency } from '../../utils/formatPlanPrice';
import { getPaymentMethods, processSubscriptionPayment } from '../../services/paymentService';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

export default function SubscriptionModal({ onClose, onConfirm }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const navigate = useNavigate();

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const plansData = await getSubscriptionPlans();
      if (plansData && plansData.length > 0) {
        setPlans(plansData);
        setSelectedPlan((prev) => prev || plansData[0].id);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des abonnements:', err);
      setError('Impossible de charger les abonnements. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    socketService.connect().catch(() => {});
    const unsub = socketService.on('subscription_plans_updated', fetchPlans);
    return () => {
      unsub?.();
    };
  }, [fetchPlans]);

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
  };

  const handleConfirm = async () => {
    if (!selectedPlan) return;
    
    setProcessing(true);
    
    try {
      // Vérifier d'abord si l'utilisateur a une méthode de paiement
      const methods = await getPaymentMethods();
      
      if (methods && methods.length > 0) {
        // Si oui, utiliser la première méthode de paiement disponible
        const paymentMethodId = methods[0].id;
        
        // Récupérer les détails du plan sélectionné
        const selectedPlanDetails = plans.find(plan => plan.id === selectedPlan);
        
        if (!selectedPlanDetails) {
          throw new Error('Détails du plan non trouvés');
        }
        
        // Traiter le paiement de l'abonnement avec les paramètres requis
        await processSubscriptionPayment({
          amount: selectedPlanDetails.price * 100, // Convertir en centimes
          currency: normalizePlanCurrency(selectedPlanDetails.currency).toLowerCase(),
          paymentMethodId: paymentMethodId,
          subscriptionId: selectedPlan, // Utiliser l'ID du plan comme ID d'abonnement
        });
        
        // Fermer la modale
        onClose();
        
        // Afficher un message de succès
        toast.success('Votre abonnement a été activé avec succès !');
        
        // Appeler la fonction de confirmation pour mettre à jour l'état parent si nécessaire
        onConfirm(selectedPlan);
      } else {
        // Si non, rediriger vers la page de paiement
        toast.info('Veuillez ajouter une méthode de paiement pour continuer');
        // Fermer la modale et rediriger
        onClose();
        navigate('/demenageur/paiements', { 
          state: { 
            fromSubscription: true,
            selectedPlan: selectedPlan 
          } 
        });
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des méthodes de paiement:', error);
      toast.error('Une erreur est survenue lors de la vérification des méthodes de paiement');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/70 p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg p-6 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-white">Chargement des offres d'abonnement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/70 p-4">
        <div className="bg-slate-800 border border-red-500 rounded-xl w-full max-w-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white text-xl font-bold">Erreur</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
          </div>
          <p className="text-red-400 mb-4">{error}</p>
          <div className="flex justify-end">
            <button 
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-slate-700 text-slate-200 hover:bg-slate-600"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/70 p-4 overflow-y-auto">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg my-8 max-h-[90vh] flex flex-col">
        <div className="p-6 overflow-y-auto flex-1">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-white text-2xl font-bold">Choisissez votre abonnement</h2>
            <button 
              onClick={onClose} 
              className="text-slate-400 hover:text-white transition-colors"
              aria-label="Fermer"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {plans.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-400">Aucun abonnement disponible pour le moment.</p>
            </div>
          ) : (
            <div className="space-y-4 mb-6">
              {plans.map((plan) => (
                <div 
                  key={plan.id}
                  className={`p-5 rounded-xl border-2 transition-all ${
                    selectedPlan === plan.id 
                      ? 'border-amber-400 bg-amber-400/10' 
                      : 'border-slate-600 hover:border-slate-500 bg-slate-700/30 hover:bg-slate-700/50 cursor-pointer'
                  }`}
                  onClick={() => handlePlanSelect(plan.id)}
                >
                  <div className="flex items-center">
                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center mr-3 ${
                      selectedPlan === plan.id ? 'border-amber-400 bg-amber-400' : 'border-slate-400'
                    }`}>
                      {selectedPlan === plan.id && (
                        <svg className="w-3 h-3 text-slate-800" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                        <span className="text-lg font-bold text-amber-400">
                          {formatPlanPrice(plan.price, plan.currency)}
                          <span className="text-sm font-normal text-slate-400 ml-1">
                            /{plan.billing_cycle === 'yearly' ? 'an' : 'mois'}
                          </span>
                        </span>
                      </div>
                      {plan.description && (
                        <p className="mt-1 text-slate-400 text-sm">{plan.description}</p>
                      )}
                      {plan.features && plan.features.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-center text-slate-300 text-sm">
                              <svg className="w-4 h-4 text-amber-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-slate-700">
            <button 
              onClick={onClose} 
              className="px-5 py-2.5 rounded-lg bg-slate-700 text-slate-200 font-medium hover:bg-slate-600 transition-colors"
            >
              Annuler
            </button>
            <button 
              onClick={handleConfirm} 
              disabled={!selectedPlan}
              className={`px-5 py-2.5 rounded-lg font-medium transition-colors ${
                selectedPlan 
                  ? 'bg-amber-500 text-slate-900 hover:bg-amber-400' 
                  : 'bg-slate-600 text-slate-400 cursor-not-allowed'
              }`}
            >
              Choisir cet abonnement
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}










